// PolicyScanner.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Copy, CheckCircle2 } from 'lucide-react';
import BuyMeACoffee from './coffee';
import NavigationPanel from './NavigationPanel';

const PolicyScanner = () => {
  const navigate = useNavigate();
  const [policyInput, setPolicyInput] = useState('');

  // Dark mode is true by default
  const [darkMode, setDarkMode] = useState(true);

  // Toggles (first four are ON by default)
  const [highlightNetworks, setHighlightNetworks] = useState(true);
  const [highlightPrivilegeEscalation, setHighlightPrivilegeEscalation] = useState(true);
  const [highlightResourceDeletion, setHighlightResourceDeletion] = useState(true);
  const [highlightDataExposure, setHighlightDataExposure] = useState(true);

  const [highlightNetworking, setHighlightNetworking] = useState(false);
  const [highlightLogging, setHighlightLogging] = useState(false);
  const [highlightFinancialImpact, setHighlightFinancialImpact] = useState(false);
  const [highlightEncryption, setHighlightEncryption] = useState(false);
  const [highlightInfrastructure, setHighlightInfrastructure] = useState(false);
  const [highlightTemporaryCredentials, setHighlightTemporaryCredentials] = useState(false);
  const [highlightOtherHarmful, setHighlightOtherHarmful] = useState(false);

  // Keep only one 'copied' state definition
  const [copied, setCopied] = useState(false);

  /**
   * Each category has:
   *   enabled: whether toggle is ON
   *   actions: array of suspicious AWS actions
   */
  const categories = [
    // --------------------------------------------------------------------------
    // 1) highlightNetworks
    // --------------------------------------------------------------------------
    {
      enabled: highlightNetworks,
      actions: [
        // Security Groups
        'ec2:AuthorizeSecurityGroupIngress',
        'ec2:RevokeSecurityGroupIngress',
        'ec2:AuthorizeSecurityGroupEgress',
        'ec2:RevokeSecurityGroupEgress',

        // VPC Peering
        'ec2:CreateVpcPeeringConnection',
        'ec2:AcceptVpcPeeringConnection',
        'ec2:DeleteVpcPeeringConnection',

        // VPC attributes
        'ec2:ModifyVpcAttribute',
        'ec2:CreateInternetGateway',
        'ec2:AttachInternetGateway',
        'ec2:DetachInternetGateway',
        'ec2:CreateNatGateway',
        'ec2:DeleteNatGateway',

        // Routing
        'ec2:CreateRoute',
        'ec2:ReplaceRoute',
        'ec2:DeleteRoute',
        'ec2:CreateRouteTable',
        'ec2:AssociateRouteTable',
        'ec2:DisassociateRouteTable',

        // Subnet and VPC modifications
        'ec2:CreateSubnet',
        'ec2:ModifySubnetAttribute',
        'ec2:AssociateVpcCidrBlock',
        'ec2:DisassociateVpcCidrBlock',

        // Elastic IP addresses
        'ec2:AllocateAddress',
        'ec2:AssociateAddress',
        'ec2:DisassociateAddress',
        'ec2:ReleaseAddress',

        // Transit Gateway
        'ec2:CreateTransitGateway',
        'ec2:DeleteTransitGateway',
        'ec2:CreateTransitGatewayPeeringAttachment',
        'ec2:AcceptTransitGatewayPeeringAttachment',
        'ec2:DeleteTransitGatewayPeeringAttachment',
      ],
    },

    // --------------------------------------------------------------------------
    // 2) highlightPrivilegeEscalation
    // --------------------------------------------------------------------------
    {
      enabled: highlightPrivilegeEscalation,
      actions: [
        // Creating or modifying IAM identities
        'iam:CreateUser',
        'iam:CreateRole',
        'iam:CreateGroup',
        'iam:CreateInstanceProfile',

        // Creating or altering policies
        'iam:CreatePolicy',
        'iam:CreatePolicyVersion',
        'iam:DeletePolicyVersion',
        'iam:SetDefaultPolicyVersion',
        'iam:AttachUserPolicy',
        'iam:AttachGroupPolicy',
        'iam:AttachRolePolicy',
        'iam:DetachUserPolicy',
        'iam:DetachGroupPolicy',
        'iam:DetachRolePolicy',
        'iam:PutUserPolicy',
        'iam:PutGroupPolicy',
        'iam:PutRolePolicy',
        'iam:PutUserPermissionsBoundary',
        'iam:PutRolePermissionsBoundary',
        'iam:DeleteRolePermissionsBoundary',
        'iam:DeleteUserPermissionsBoundary',

        // Modify trust relationships & pass roles
        'iam:UpdateAssumeRolePolicy',
        'iam:PassRole',

        // Updating roles/accounts
        'iam:UpdateRole',
        'iam:UpdateAccountPasswordPolicy',
        'iam:UpdateLoginProfile',
        'iam:UpdateRoleDescription',

        // Create or update credentials
        'iam:CreateAccessKey',
        'iam:UpdateAccessKey',
        'iam:CreateLoginProfile',
        'iam:GenerateServiceLastAccessedDetails',

        // Group membership changes
        'iam:AddUserToGroup',
        'iam:RemoveUserFromGroup',

        // Potentially destructive IAM actions
        'iam:DeletePolicy',
        'iam:DeleteUser',
        'iam:DeleteRole',
        'iam:DeleteGroup',
        'iam:RemoveRoleFromInstanceProfile',
        'iam:AddRoleToInstanceProfile',

        // Federation/IdP setup
        'iam:CreateSAMLProvider',
        'iam:UpdateSAMLProvider',
        'iam:CreateOpenIDConnectProvider',
        'iam:UpdateOpenIDConnectProviderThumbprint',
      ],
    },

    // --------------------------------------------------------------------------
    // 3) highlightResourceDeletion
    // --------------------------------------------------------------------------
    {
      enabled: highlightResourceDeletion,
      actions: [
        // S3
        's3:DeleteBucket',
        's3:DeleteObject',
        's3:DeleteBucketPolicy',
        's3:DeleteAccessPoint',
        's3:DeleteAccessPointPolicy',

        // Users
        'iam:DeleteUser',

        // EC2
        'ec2:TerminateInstances',
        'ec2:DeleteSnapshot',
        'ec2:DeleteInternetGateway',
        'ec2:DeleteVpc',
        'ec2:DeleteSubnet',
        'ec2:DetachInternetGateway',
        'ec2:ReleaseAddress',
        'ec2:DeleteRoute',
        'ec2:DeleteRouteTable',

        // RDS
        'rds:DeleteDBInstance',
        'rds:DeleteDBCluster',

        // DynamoDB
        'dynamodb:DeleteTable',
        'dynamodb:DeleteItem',

        // Lambda
        'lambda:DeleteFunction',

        // CloudFormation
        'cloudformation:DeleteStack',

        // CloudTrail
        'cloudtrail:DeleteTrail',
        'cloudtrail:DeleteChannel',
        'cloudtrail:DeleteEventDataStore',

        // Glacier
        'glacier:DeleteVault',

        // CloudWatch
        'cloudwatch:DeleteAlarms',
        'cloudwatch:DeleteDashboards',

        // Logs
        'logs:DeleteLogGroup',
        'logs:DeleteLogStream',
        'logs:DeleteMetricFilter',
        'logs:DeleteSubscriptionFilter',

        // Route53
        'route53:DeleteHostedZone',
        'route53:DeleteHealthCheck',

        // Elastic Beanstalk
        'elasticbeanstalk:TerminateEnvironment',

        // EKS
        'eks:DeleteCluster',
        'eks:DeleteNodegroup',

        // ECS
        'ecs:DeleteService',
        'ecs:DeleteCluster',

        // ECR
        'ecr:DeleteRepository',

        // EMR
        'emr:TerminateJobFlows',

        // ElastiCache
        'elasticache:DeleteCacheCluster',

        // AutoScaling
        'autoscaling:DeleteAutoScalingGroup',
        'autoscaling:DeleteLaunchConfiguration',

        // SNS / SQS
        'sns:DeleteTopic',
        'sns:Unsubscribe',
        'sqs:DeleteQueue',
        'sqs:PurgeQueue',

        // Glue
        'glue:DeleteDatabase',
        'glue:DeleteTable',
        'glue:DeleteCrawler',
      ],
    },

    // --------------------------------------------------------------------------
    // 4) highlightDataExposure
    // --------------------------------------------------------------------------
    {
      enabled: highlightDataExposure,
      actions: [
        // S3
        's3:GetObject',
        's3:GetObjectAcl',
        's3:ListBucket',
        's3:GetBucketAcl',
        's3:GetBucketPolicy',
        's3:GetBucketPolicyStatus',
        's3:GetBucketLocation',
        's3:GetBucketPublicAccessBlock',

        // RDS
        'rds:DescribeDBLogFiles',
        'rds:DownloadDBLogFilePortion',

        // Secrets Manager
        'secretsmanager:GetSecretValue',
        'secretsmanager:DescribeSecret',
        'secretsmanager:ListSecrets',

        // SSM Parameter Store
        'ssm:GetParameter',
        'ssm:GetParameters',
        'ssm:GetParameterHistory',
        'ssm:GetParametersByPath',
        'ssm:DescribeParameters',

        // DynamoDB
        'dynamodb:GetItem',
        'dynamodb:Query',
        'dynamodb:Scan',

        // CloudTrail
        'cloudtrail:GetTrailStatus',
        'cloudtrail:LookupEvents',

        // EC2
        'ec2:DescribeInstances',
        'ec2:DescribeVolumes',
        'ec2:DescribeSnapshots',
        'ec2:DescribeSecurityGroups',
        'ec2:DescribeSubnets',
        'ec2:DescribeAddresses',
        'ec2:DescribeNatGateways',
        'ec2:DescribeVpcPeeringConnections',

        // EKS
        'eks:DescribeCluster',
        'eks:ListClusters',
        'eks:DescribeNodegroup',
        'eks:ListNodegroups',

        // KMS
        'kms:Decrypt',
        'kms:DescribeKey',
        'kms:ListAliases',
        'kms:ListKeys',

        // Logs
        'logs:GetLogEvents',
        'logs:FilterLogEvents',
        'logs:DescribeLogGroups',
        'logs:DescribeLogStreams',

        // CloudWatch
        'cloudwatch:GetMetricStatistics',
        'cloudwatch:DescribeAlarms',
        'cloudwatch:DescribeAlarmHistory',

        // Athena
        'athena:StartQueryExecution',
        'athena:GetQueryResults',

        // Glue
        'glue:GetDatabase',
        'glue:GetTables',
        'glue:GetPartitions',

        // Redshift
        'redshift:DescribeClusters',
        'redshift:GetClusterCredentials',

        // Route53
        'route53:ListHostedZones',
        'route53:GetHostedZone',

        // IAM (exposure of credentials/policies)
        'iam:GetUser',
        'iam:GetRole',
        'iam:GetPolicy',
        'iam:GetPolicyVersion',
        'iam:ListAccessKeys',
        'iam:GenerateCredentialReport',
        'iam:GetCredentialReport',
      ],
    },

    // --------------------------------------------------------------------------
    // 5) highlightNetworking (same "networks" category actions, repeated if desired)
    // --------------------------------------------------------------------------
    {
      enabled: highlightNetworking,
      actions: [
        // Security Groups
        'ec2:AuthorizeSecurityGroupIngress',
        'ec2:RevokeSecurityGroupIngress',
        'ec2:AuthorizeSecurityGroupEgress',
        'ec2:RevokeSecurityGroupEgress',

        // VPC Peering
        'ec2:CreateVpcPeeringConnection',
        'ec2:AcceptVpcPeeringConnection',
        'ec2:DeleteVpcPeeringConnection',

        // VPC attributes
        'ec2:ModifyVpcAttribute',
        'ec2:CreateInternetGateway',
        'ec2:AttachInternetGateway',
        'ec2:DetachInternetGateway',
        'ec2:CreateNatGateway',
        'ec2:DeleteNatGateway',

        // Routing
        'ec2:CreateRoute',
        'ec2:ReplaceRoute',
        'ec2:DeleteRoute',
        'ec2:CreateRouteTable',
        'ec2:AssociateRouteTable',
        'ec2:DisassociateRouteTable',

        // Subnet and VPC modifications
        'ec2:CreateSubnet',
        'ec2:ModifySubnetAttribute',
        'ec2:AssociateVpcCidrBlock',
        'ec2:DisassociateVpcCidrBlock',

        // Elastic IP addresses
        'ec2:AllocateAddress',
        'ec2:AssociateAddress',
        'ec2:DisassociateAddress',
        'ec2:ReleaseAddress',

        // Transit Gateway
        'ec2:CreateTransitGateway',
        'ec2:DeleteTransitGateway',
        'ec2:CreateTransitGatewayPeeringAttachment',
        'ec2:AcceptTransitGatewayPeeringAttachment',
        'ec2:DeleteTransitGatewayPeeringAttachment',
      ],
    },

    // --------------------------------------------------------------------------
    // 6) highlightLogging
    // --------------------------------------------------------------------------
    {
      enabled: highlightLogging,
      actions: [
        // CloudTrail
        'cloudtrail:StopLogging',
        'cloudtrail:DeleteTrail',
        'cloudtrail:DeleteEventDataStore',
        'cloudtrail:DeleteChannel',
        'cloudtrail:UpdateTrail',
        'cloudtrail:PutInsightSelectors',
        'cloudtrail:StartLogging',

        // CloudWatch
        'cloudwatch:DeleteAlarms',
        'cloudwatch:DisableAlarmActions',
        'cloudwatch:DeleteDashboards',

        // CloudWatch Logs
        'logs:DeleteLogGroup',
        'logs:DeleteLogStream',
        'logs:DeleteMetricFilter',
        'logs:DeleteSubscriptionFilter',
        'logs:PutRetentionPolicy',
        'logs:PutMetricFilter',
        'logs:PutSubscriptionFilter',
        'logs:CreateLogStream',
        'logs:AssociateKmsKey',
        'logs:DisassociateKmsKey',

        // Potential interference with logging
        'cloudwatch:StopMetricStreams',
        'cloudwatch:DeleteMetricStream',
      ],
    },

    // --------------------------------------------------------------------------
    // 7) highlightFinancialImpact
    // --------------------------------------------------------------------------
    {
      enabled: highlightFinancialImpact,
      actions: [
        // Launching or modifying compute resources
        'ec2:RunInstances',
        'ec2:StartInstances',
        'ec2:RunScheduledInstances',
        'ec2:CreateSpotFleet',
        'autoscaling:CreateAutoScalingGroup',
        'autoscaling:UpdateAutoScalingGroup',

        // RDS
        'rds:CreateDBInstance',
        'rds:ModifyDBInstance',
        'rds:CreateDBCluster',
        'rds:ModifyDBCluster',

        // Lambda
        'lambda:InvokeFunction',
        'lambda:CreateFunction',

        // EMR
        'emr:RunJobFlow',
        'emr:AddInstanceGroups',
        'emr:AddInstanceFleet',

        // Redshift
        'redshift:CreateCluster',
        'redshift:ResizeCluster',

        // ElastiCache
        'elasticache:CreateCacheCluster',
        'elasticache:ModifyCacheCluster',

        // ECS
        'ecs:CreateCluster',
        'ecs:CreateService',
        'ecs:UpdateService',

        // EKS
        'eks:CreateCluster',
        'eks:UpdateClusterVersion',

        // CloudFormation
        'cloudformation:CreateStack',
        'cloudformation:UpdateStack',

        // Kinesis
        'kinesis:CreateStream',
        'kinesis:PutRecord',
        'kinesis:PutRecords',

        // Glue
        'glue:CreateJob',
        'glue:StartJobRun',

        // Batch
        'batch:SubmitJob',
      ],
    },

    // --------------------------------------------------------------------------
    // 8) highlightEncryption
    // --------------------------------------------------------------------------
    {
      enabled: highlightEncryption,
      actions: [
        'kms:CreateKey',
        'kms:ScheduleKeyDeletion',
        'kms:DisableKey',
        'kms:DisableKeyRotation',
        'kms:EnableKeyRotation',
        'kms:RevokeGrant',
        'kms:CreateGrant',
        'kms:Encrypt',
        'kms:Decrypt',
        'kms:GenerateDataKey',
        'kms:GenerateDataKeyWithoutPlaintext',
        'kms:CreateAlias',
        'kms:UpdateAlias',
        'kms:DeleteAlias',
        'kms:DescribeKey',
        'kms:ListKeys',
        'kms:ListAliases',
      ],
    },

    // --------------------------------------------------------------------------
    // 9) highlightInfrastructure
    // --------------------------------------------------------------------------
    {
      enabled: highlightInfrastructure,
      actions: [
        // CloudFormation
        'cloudformation:UpdateStack',
        'cloudformation:DeleteStack',

        // EC2
        'ec2:ModifyInstanceAttribute',
        'ec2:CreateSnapshot',
        'ec2:DeleteSnapshot',
        'ec2:ModifyVolume',
        'ec2:ModifyImageAttribute',

        // Route53
        'route53:ChangeResourceRecordSets',

        // ECS
        'ecs:UpdateService',

        // EKS
        'eks:UpdateClusterVersion',

        // Auto Scaling
        'autoscaling:UpdateAutoScalingGroup',

        // Elastic Load Balancing
        'elasticloadbalancing:CreateLoadBalancer',
        'elasticloadbalancing:DeleteLoadBalancer',
        'elasticloadbalancing:ModifyLoadBalancerAttributes',

        // CloudFront
        'cloudfront:CreateDistribution',
        'cloudfront:UpdateDistribution',
        'cloudfront:DeleteDistribution',
      ],
    },

    // --------------------------------------------------------------------------
    // 10) highlightTemporaryCredentials
    // --------------------------------------------------------------------------
    {
      enabled: highlightTemporaryCredentials,
      actions: [
        'sts:AssumeRole',
        'sts:AssumeRoleWithWebIdentity',
        'sts:AssumeRoleWithSAML',
        'sts:GetFederationToken',
        'sts:GetSessionToken',
      ],
    },

    // --------------------------------------------------------------------------
    // 11) highlightOtherHarmful
    // --------------------------------------------------------------------------
    {
      enabled: highlightOtherHarmful,
      actions: [
        // Potential data or resource disruptions
        'sqs:PurgeQueue',
        'sqs:DeleteQueue',
        'sns:DeleteTopic',
        'sns:Unsubscribe',
        'glue:DeleteDatabase',
        'glue:DeleteTable',
        'glue:DeleteCrawler',
        'glacier:DeleteVault',
        'elasticbeanstalk:TerminateEnvironment',
        'iam:DeleteUser',
        'iam:DetachRolePolicy',
        'iam:DetachGroupPolicy',
        'iam:DetachUserPolicy',
        'cloudwatch:DeleteDashboards',
        'logs:DeleteLogGroup',
        'logs:DeleteLogStream',
      ],
    },
  ];


  /**
   * highlightPolicyLineBased
   */
  const highlightPolicyLineBased = useCallback((text) => {
    if (!text) return '';

    const lines = text.split('\n');
    const highlighted = lines.map((line) => {
      const escapedLine = escapeHtml(line);

      let shouldHighlight = false;
      // Check each enabled category
      for (const cat of categories) {
        if (!cat.enabled) continue;
        for (const action of cat.actions) {
          if (line.includes(action)) {
            shouldHighlight = true;
            break;
          }
        }
        if (shouldHighlight) break;
      }

      if (shouldHighlight) {
        return `<div class="bg-amber-500 text-gray-900 px-1 rounded mb-1">${escapedLine}</div>`;
      } else {
        return `<div class="mb-1">${escapedLine}</div>`;
      }
    });

    return highlighted.join('');
  }, [categories]);

  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const getHighlightedHTML = useCallback(() => {
    return highlightPolicyLineBased(policyInput);
  }, [policyInput, highlightPolicyLineBased]);

  const handleInput = (e) => {
    setPolicyInput(e.currentTarget.innerText);
  };

  const copyToClipboard = useCallback(() => {
    if (!policyInput) return;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = policyInput;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.top = '50%';
      textarea.style.left = '50%';
      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Failed to copy - please try selecting and copying manually');
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [policyInput]);

  const isYesNo = (val) => (val ? 'Yes' : 'No');

  return (
    <>
      {/* 1) Embedded style for placeholder when the div is empty */}
      <style>
        {`
          .policy-input:empty::before {
            content: "Paste your Amazon IAM policy here (JSON format)...";
            color: #999;
            pointer-events: none;
          }
        `}
      </style>

      <NavigationPanel darkMode={darkMode} setDarkMode={setDarkMode} />

      <div
        className={`
          min-h-screen p-4 md:p-8 transition-colors
          ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}
        `}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div
              className={`
                md:col-span-2
                rounded-lg shadow p-8 space-y-4 border border-transparent
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
                min-h-[600px]
              `}
            >
              <div className="flex items-center justify-between">
                <label className="text-lg font-semibold">Input Policy</label>
                <button
                  onClick={copyToClipboard}
                  className="
                    inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors
                    bg-[#FF9900] text-white hover:bg-[#FFB84D]
                  "
                >
                  {copied ? (
                    <>
                      <CheckCircle2 size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy Policy
                    </>
                  )}
                </button>
              </div>

              {/* 2) Add the "policy-input" class for the placeholder rule */}
              <div
                contentEditable
                spellCheck="false"
                onInput={handleInput}
                dangerouslySetInnerHTML={{ __html: getHighlightedHTML() }}
                className={`
                  policy-input
                  w-full h-[650px] p-3 border border-amber-200 rounded-md font-mono text-sm
                  focus:outline-none resize-none transition-colors
                  scrollbar-thin
                  scrollbar-thumb-gray-500
                  scrollbar-track-gray-300
                  hover:scrollbar-thumb-gray-600
                  ${darkMode
                    ? 'bg-gray-700 text-gray-100 border-gray-600 scrollbar-track-gray-700 scrollbar-thumb-gray-500 hover:scrollbar-thumb-gray-400'
                    : 'bg-gray-200 text-gray-800 border-gray-300'
                  }
                  whitespace-pre-wrap overflow-auto
                `}
              />
            </div>

            <div
              className={`
                md:col-span-1
                rounded-lg shadow p-8 space-y-4 border border-transparent
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
                min-h-[600px]
              `}
            >
              <h2 className="text-lg font-semibold">Highlight permissions for</h2>

              <div className="flex flex-col space-y-4">
                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Network Resources</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightNetworks}
                    onChange={() => setHighlightNetworks(!highlightNetworks)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      peer-focus:outline-none
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{isYesNo(highlightNetworks)}</span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Privilege Escalation</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightPrivilegeEscalation}
                    onChange={() => setHighlightPrivilegeEscalation(!highlightPrivilegeEscalation)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">
                    {isYesNo(highlightPrivilegeEscalation)}
                  </span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Resource Deletion</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightResourceDeletion}
                    onChange={() => setHighlightResourceDeletion(!highlightResourceDeletion)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{isYesNo(highlightResourceDeletion)}</span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Data Exposure</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightDataExposure}
                    onChange={() => setHighlightDataExposure(!highlightDataExposure)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{isYesNo(highlightDataExposure)}</span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Networking</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightNetworking}
                    onChange={() => setHighlightNetworking(!highlightNetworking)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{isYesNo(highlightNetworking)}</span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Logging</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightLogging}
                    onChange={() => setHighlightLogging(!highlightLogging)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{isYesNo(highlightLogging)}</span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Financial Impact</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightFinancialImpact}
                    onChange={() => setHighlightFinancialImpact(!highlightFinancialImpact)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">
                    {isYesNo(highlightFinancialImpact)}
                  </span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Encryption</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightEncryption}
                    onChange={() => setHighlightEncryption(!highlightEncryption)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">{isYesNo(highlightEncryption)}</span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Infrastructure</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightInfrastructure}
                    onChange={() => setHighlightInfrastructure(!highlightInfrastructure)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">
                    {isYesNo(highlightInfrastructure)}
                  </span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Temporary Credentials</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightTemporaryCredentials}
                    onChange={() => setHighlightTemporaryCredentials(!highlightTemporaryCredentials)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">
                    {isYesNo(highlightTemporaryCredentials)}
                  </span>
                </label>

                <label className="flex items-center cursor-pointer space-x-2">
                  <span className="text-sm font-medium">Other Harmful Actions</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={highlightOtherHarmful}
                    onChange={() => setHighlightOtherHarmful(!highlightOtherHarmful)}
                  />
                  <div
                    className={`
                      w-11 h-6 rounded-full peer
                      transition-colors 
                      bg-gray-300 dark:bg-gray-600
                      peer-checked:bg-[#FF9900]
                      relative
                      after:content-['']
                      after:absolute
                      after:top-[2px]
                      after:left-[2px]
                      after:bg-white
                      after:border-gray-300
                      after:border
                      after:rounded-full
                      after:h-5
                      after:w-5
                      after:transition-all
                      peer-checked:after:translate-x-full
                      peer-checked:after:border-white
                    `}
                  />
                  <span className="ml-2 text-sm">
                    {isYesNo(highlightOtherHarmful)}
                  </span>
                </label>
              </div>

              <div className="pt-4">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Tip: Use the toggle switches to highlight *lines* containing
                  certain AWS actions. For instance, enabling “Network Resources?”
                  highlights any line referencing known network configuration actions.
                </p>
                <p
                  className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  You can also manually select the text and press{' '}
                  <kbd
                    className="border bg-gray-100 px-1 text-xs font-mono rounded
                               dark:bg-gray-700 dark:border-gray-500"
                  >
                    Ctrl + C
                  </kbd>{' '}
                  (or Cmd + C on Mac) to copy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BuyMeACoffee />
    </>
  );
};

export default PolicyScanner;